import { useState } from 'react'
import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client'
import type { Adapter } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { GET_ME } from 'api/auth/me'
import { LINK_WALLET } from 'api/wallets/link-wallet'
import { LINK_WALLET_CHALLENGE } from 'api/wallets/link-wallet-challenge'
import {
  connectSelectedSolanaWallet,
  isUserRejectedWalletError,
  signChallengeWithAdapter
} from 'features/auth/utils/connect-solana-adapter'
import type { SolanaConnectorProvider } from 'features/auth/utils/get-solana-connector-providers'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { userVar } from 'shared/store/user'
import { withWalletChoice } from 'shared/store/wallet-choice'
import { isWalletLinkingVar } from '../store/linking'
import {
  notifyAlreadyLinked,
  notifyLinkWalletError,
  notifySignatureDeclined,
  notifyWalletLinked
} from '../utils/notify'

/**
 * Links a Solana wallet to the signed-in account (FE-305, backend DEV-321):
 * `linkWalletChallenge` → the wallet signs the SIWS-style message →
 * `linkWallet` → `me` refetched. The wallet stays connected and plays: its
 * app becomes the user's wallet choice, so the Privy auto-selection does not
 * switch back. A wallet that already belongs to another account (including
 * an auto-created Anonymous one) is rejected by the backend, and a failed
 * attempt restores the previous choice.
 */
// eslint-disable-next-line max-statements
export const useLinkWallet = () => {
  const client = useApolloClient()
  const [requestChallenge] = useMutation(LINK_WALLET_CHALLENGE)
  const [commitLinkWallet] = useMutation(LINK_WALLET)
  const { wallet, wallets, select } = useWallet()
  const user = useReactiveVar(userVar)
  const { linkedAddresses } = usePlayWallet()
  const [isLinking, setIsLinking] = useState(false)

  // eslint-disable-next-line max-statements
  const linkAdapterWallet = async (adapter: Adapter, walletAddress: string) => {
    if (linkedAddresses.includes(walletAddress)) {
      notifyAlreadyLinked(walletAddress)
      return
    }

    const { data } = await requestChallenge({ variables: { walletAddress } })
    const challenge = data?.linkWalletChallenge.challenge
    if (!challenge) throw new Error('The server returned no message to sign')

    const signature = await signChallengeWithAdapter(adapter, challenge)
    if (!signature) throw new Error('The wallet did not sign the message')

    const { data: linked } = await commitLinkWallet({
      variables: { walletAddress, signature }
    })
    if (!linked) throw new Error('Wallet was not linked')

    await client.query({ query: GET_ME, fetchPolicy: 'network-only' })
    notifyWalletLinked(linked.linkWallet.address)
  }

  const runLinking = async (
    walletName: string | undefined,
    link: () => Promise<void>
  ) => {
    if (isLinking) return

    setIsLinking(true)
    isWalletLinkingVar(true)
    try {
      if (!user || !walletName) throw new Error('Wallet not connected')
      await withWalletChoice(user.id, walletName, link)
    } catch (error) {
      if (isUserRejectedWalletError(error)) notifySignatureDeclined()
      else notifyLinkWalletError(error)
    } finally {
      setIsLinking(false)
      isWalletLinkingVar(false)
    }
  }

  /** Links the wallet that is connected right now. */
  const linkConnectedWallet = () =>
    runLinking(wallet?.adapter.name, async () => {
      const adapter = wallet?.adapter
      const walletAddress = adapter?.publicKey?.toBase58()
      if (!adapter || !walletAddress) throw new Error('Wallet not connected')

      await linkAdapterWallet(adapter, walletAddress)
    })

  /**
   * Re-authorizes the account currently active in the wallet app (so the user
   * can switch accounts there first) and links it.
   */
  const linkProviderWallet = (provider: SolanaConnectorProvider) =>
    runLinking(provider.walletName, async () => {
      const connected = await connectSelectedSolanaWallet(
        wallets,
        provider.walletName,
        select
      )

      await linkAdapterWallet(connected.adapter, connected.walletAddress)
    })

  return { linkConnectedWallet, linkProviderWallet, isLinking }
}
